import {
  daysInMonth,
  formatDate,
  formatDateTime,
  formatTime,
  isValidDate,
  MAX_YEAR,
  MIN_YEAR,
  parseDate,
  parseDateTime,
  parseTime,
  type CalendarDate,
  type WallTime,
} from './temporal';

/**
 * The segmented field's model, after Chromium's `DateTimeNumericFieldElement`: one number per
 * segment, typed through a type-ahead buffer, stepped with wrap-around, and turned into the
 * native value string only when every segment holds a value and the day exists.
 */

export type SegmentType = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'period';
export type TemporalKind = 'date' | 'time' | 'datetime';

/**
 * What each segment holds. `hour` is 1–12 on a 12-hour clock (with `period` 0 = AM, 1 = PM) and
 * 0–23 on a 24-hour clock.
 */
export type SegmentValues = Partial<Record<SegmentType, number>>;

export interface SegmentOptions {
  hour12: boolean;
  /** Minutes the minute segment steps by (`step` seconds / 60, at least 1). */
  minuteStep: number;
}

export interface SegmentLimits {
  min: number;
  max: number;
}

const DATE_TYPES: readonly SegmentType[] = ['year', 'month', 'day'];

export function segmentTypes(kind: TemporalKind, hour12: boolean): SegmentType[] {
  const time: SegmentType[] = hour12 ? ['hour', 'minute', 'period'] : ['hour', 'minute'];
  if (kind === 'date') return [...DATE_TYPES];
  if (kind === 'time') return time;
  return [...DATE_TYPES, ...time];
}

/** The hard limits typing is clamped to (Chromium's `hard_limits_`). */
export function typingLimits(type: SegmentType, options: SegmentOptions): SegmentLimits {
  switch (type) {
    case 'year':
      return { min: MIN_YEAR, max: MAX_YEAR };
    case 'month':
      return { min: 1, max: 12 };
    case 'day':
      return { min: 1, max: 31 };
    case 'hour':
      return options.hour12 ? { min: 1, max: 12 } : { min: 0, max: 23 };
    case 'minute':
      return { min: 0, max: 59 };
    case 'period':
      return { min: 0, max: 1 };
  }
}

/** The range arrows step and wrap within: a day wraps at the chosen month's length. */
export function stepLimits(
  type: SegmentType,
  values: SegmentValues,
  options: SegmentOptions,
): SegmentLimits {
  if (type === 'day' && values.month !== undefined) {
    // Without a year, February may still be in a leap year.
    return { min: 1, max: daysInMonth(values.year ?? 2000, values.month) };
  }
  return typingLimits(type, options);
}

export interface TypeAheadResult {
  /** The digits typed into this segment so far. */
  buffer: string;
  /** The segment's value, or undefined while the digits are below the minimum (a lone 0). */
  value: number | undefined;
  /** Whether focus moves to the next segment: no further digit could make a valid number. */
  advance: boolean;
}

/**
 * One digit typed into a numeric segment. Once the buffer holds as many digits as the maximum
 * has, the oldest is dropped; a number above the maximum is clamped to it (month 1, 3 → 12).
 */
export function typeDigit(buffer: string, digit: number, limits: SegmentLimits): TypeAheadResult {
  const maxLength = String(limits.max).length;
  const kept = buffer.length >= maxLength ? buffer.slice(buffer.length - (maxLength - 1)) : buffer;
  const next = `${kept}${digit}`;
  const number = Number(next);
  return {
    buffer: next,
    value: number >= limits.min ? Math.min(number, limits.max) : undefined,
    advance: next.length >= maxLength || number * 10 > limits.max,
  };
}

/**
 * Up (`+1`) or Down (`-1`) on a segment. An empty segment starts at the bottom (Up) or top
 * (Down) of its range, a year at `currentYear`; values wrap around, minutes move by the step.
 */
export function stepSegment(
  type: SegmentType,
  values: SegmentValues,
  delta: 1 | -1,
  options: SegmentOptions,
  currentYear: number,
): number {
  const current = values[type];
  if (type === 'period') {
    if (current === undefined) return delta > 0 ? 0 : 1;
    return current === 0 ? 1 : 0;
  }
  if (type === 'year' && current === undefined) return currentYear;
  const { min, max } = stepLimits(type, values, options);
  const step = type === 'minute' ? Math.max(1, options.minuteStep) : 1;
  const top = min + Math.floor((max - min) / step) * step;
  if (current === undefined) return delta > 0 ? min : top;
  if (delta > 0) {
    const next = min + (Math.floor((current - min) / step) + 1) * step;
    return next > max ? min : next;
  }
  const next = min + (Math.ceil((current - min) / step) - 1) * step;
  return next < min || next > max ? top : next;
}

/** The hour on a 24-hour clock, once the hour (and on a 12-hour clock the period) is set. */
export function hour24(values: SegmentValues, options: SegmentOptions): number | undefined {
  if (values.hour === undefined) return undefined;
  if (!options.hour12) return values.hour;
  if (values.period === undefined) return undefined;
  return (values.hour % 12) + (values.period === 1 ? 12 : 0);
}

export function valuesToDate(values: SegmentValues): CalendarDate | null {
  const { year, month, day } = values;
  if (year === undefined || month === undefined || day === undefined) return null;
  const date = { year, month, day };
  return isValidDate(date) ? date : null;
}

export function valuesToTime(values: SegmentValues, options: SegmentOptions): WallTime | null {
  const hour = hour24(values, options);
  if (hour === undefined || values.minute === undefined) return null;
  return { hour, minute: values.minute };
}

export function dateToValues(date: CalendarDate): SegmentValues {
  return { year: date.year, month: date.month, day: date.day };
}

export function timeToValues(time: WallTime, options: SegmentOptions): SegmentValues {
  if (!options.hour12) return { hour: time.hour, minute: time.minute };
  return { hour: time.hour % 12 || 12, minute: time.minute, period: time.hour >= 12 ? 1 : 0 };
}

/** The native value for the segments; empty while any segment is missing or the day is impossible. */
export function composeValue(
  kind: TemporalKind,
  values: SegmentValues,
  options: SegmentOptions,
): string {
  if (kind === 'date') {
    const date = valuesToDate(values);
    return date ? formatDate(date) : '';
  }
  if (kind === 'time') {
    const time = valuesToTime(values, options);
    return time ? formatTime(time) : '';
  }
  const date = valuesToDate(values);
  const time = valuesToTime(values, options);
  return date && time ? formatDateTime(date, time) : '';
}

/** Segments for a native value; none for an empty or malformed one. */
export function decomposeValue(
  kind: TemporalKind,
  value: string | null | undefined,
  options: SegmentOptions,
): SegmentValues {
  if (kind === 'date') {
    const date = parseDate(value);
    return date ? dateToValues(date) : {};
  }
  if (kind === 'time') {
    const time = parseTime(value);
    return time ? timeToValues(time, options) : {};
  }
  const parsed = parseDateTime(value);
  return parsed ? { ...dateToValues(parsed.date), ...timeToValues(parsed.time, options) } : {};
}

/** Minutes per step for a native `step` in seconds; steps below a minute fall back to 1. */
export function minuteStepFor(step: number | string | undefined): number {
  const seconds = Number(step ?? 60);
  if (!Number.isFinite(seconds) || seconds < 60) return 1;
  return Math.max(1, Math.floor(seconds / 60));
}
